import type {
  ImageSizes as LazyImageSizes,
  ResponsiveLazyImageProps,
} from '../../components/LazyImage/types';
import type { ImageSizes } from '../../types/Image';
import { getInteger } from '../number';
import { CDN_BASE_URL } from './constants';
import { transformPrismicImageUrl } from './prismic';

interface TransformImageUrlOptions {
  width: number;
  height?: number;
  format?: string;
}

interface GetImageURLForCDNOptions {
  service: 'attribute' | 'product';
  height: number;
  width: number;
  imageName: string;
  format?: string;
}

const IMAGE_SIZE_REGEX = /(\/original\/)|(\/\d+x\d+\/)/;
const ALTERNATIVE_SIZE_REGEX = /\d+:\d+/;

const OUTPUT_FORMAT_REGEX = /output-format=([^&]+)/;

export const isOriginalImageUrl = (src: string) =>
  IMAGE_SIZE_REGEX.exec(src) || ALTERNATIVE_SIZE_REGEX.exec(src);

export const hasOutputFormat = (src: string) => OUTPUT_FORMAT_REGEX.exec(src);

export const replaceOutputFormat = (src: string, format: string) =>
  src.replace(OUTPUT_FORMAT_REGEX, `output-format=${format.toUpperCase()}`);

const removeBackSlashes = (image: string) => image.replace('/', '');

export const getImageURLForCDN = ({
  service,
  height,
  width,
  imageName,
  format = 'png',
}: GetImageURLForCDNOptions) => {
  const dimension = `${height}x${width}`;
  const formattedImageName = removeBackSlashes(imageName);

  return `${CDN_BASE_URL}/images/media/catalog/${service}/${dimension}/${format}/${formattedImageName}`;
};

export const transformImageUrl = (url: string, { width, format }: TransformImageUrlOptions) =>
  isOriginalImageUrl(url)
    ? transformOriginalImageUrl(url, {
        width,
        ...(format && { format: format.toLowerCase() }),
      })
    : transformPrismicImageUrl(url, width);

export const transformOriginalImageUrl = (
  url: string,
  { width, height, format }: TransformImageUrlOptions
) => {
  if (format) {
    url = getWithReplacedExt(url, format);
  }

  if (IMAGE_SIZE_REGEX.test(url)) {
    return url.replace(IMAGE_SIZE_REGEX, `/${width}x${height ?? width}/`);
  }

  return url.replace(ALTERNATIVE_SIZE_REGEX, `${width}:${height ?? width}`);
};

const getAllPixelRatioImgSizes = (url: string, size: number): LazyImageSizes => ({
  x1: transformOriginalImageUrl(url, { width: size }),
  x2: transformOriginalImageUrl(url, { width: 2 * size }),
  x3: transformOriginalImageUrl(url, { width: 3 * size }),
});

export const urlToLazyImageProps = (
  url: string,
  { thumbnail = 16, mobile, tablet, desktop, large_desktop }: ImageSizes
): ResponsiveLazyImageProps => ({
  thumbnail: transformOriginalImageUrl(url, { width: thumbnail }),
  mobile: getAllPixelRatioImgSizes(url, mobile),
  tablet: getAllPixelRatioImgSizes(url, tablet),
  desktop: getAllPixelRatioImgSizes(url, desktop),
  large_desktop: getAllPixelRatioImgSizes(url, large_desktop),
});

export const getDimensions = (url: string) => {
  const matches = /(?:w{1}\={1}|\/{1})(\d+)(?:\&h=|x)(\d+)\/?/.exec(url);

  if (!matches) {
    return null;
  }

  return { width: getInteger(matches[1]), height: getInteger(matches[2]) };
};

const getWithReplacedExt = (src: string, format: string) => {
  if (hasOutputFormat(src)) {
    return replaceOutputFormat(src, format);
  }

  return src.slice(0, src.lastIndexOf('.') + 1) + format;
};
